/**
 * CatalogTaxonomyModal — sektör ve kategori yönetiminin tek yeri.
 *
 * Solda sektör listesi, sağda seçili sektörün 2 seviyeli kategori ağacı. Her seviyede
 * ekleme, yeniden adlandırma ve silme var.
 *
 * Lookup tablosu tercih etmemizin asıl kazancı burada: bir kategori adını düzeltmek tek
 * satır güncellemesi, 23 bin ürüne dokunmaya gerek yok.
 *
 * Silme soft-delete (`IsDeleted`) ile yapılır. Altında ürün veya alt kategori bulunan bir
 * kaydı backend silmez; kullanıcıya önce taşıma yapması söylenir — aksi halde ürünler
 * erişilemez bir kategoriye bağlı kalır.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';

import CatalogService from '../../../../services/CatalogService';
import type { CatalogCategory, CatalogSector } from '../types/catalog.types';

type EditTarget = { kind: 'sector' | 'category'; id: number } | null;
type DeleteTarget = { kind: 'sector' | 'category'; id: number; name: string } | null;

export interface CatalogTaxonomyModalProps {
  open: boolean;
  onClose: () => void;
  /** Taksonomi değiştiyse ana sayfa sektörleri ve listeyi tazelesin. */
  onChanged: () => void;
}

const CatalogTaxonomyModal: React.FC<CatalogTaxonomyModalProps> = ({
  open,
  onClose,
  onChanged,
}) => {
  const [sectors, setSectors] = useState<CatalogSector[]>([]);
  const [categories, setCategories] = useState<CatalogCategory[]>([]);
  const [selectedSectorId, setSelectedSectorId] = useState<number | null>(null);

  const [sectorsLoading, setSectorsLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [newSectorName, setNewSectorName] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  /** Yeni kategori hangi üst kategorinin altına açılacak; null = üst seviye. */
  const [newCategoryParentId, setNewCategoryParentId] = useState<number | null>(null);

  const [editTarget, setEditTarget] = useState<EditTarget>(null);
  const [editName, setEditName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget>(null);

  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  /** Modal içinde bir şey değiştiyse kapanışta ana sayfaya haber ver. */
  const [dirty, setDirty] = useState(false);

  const selectedSector = useMemo(
    () => sectors.find((s) => s.id === selectedSectorId) ?? null,
    [sectors, selectedSectorId]
  );

  const loadSectors = useCallback(async () => {
    setSectorsLoading(true);
    try {
      const rows = await CatalogService.getSectors(true);
      setSectors(rows);
      return rows;
    } catch (err: any) {
      setError(err?.message || 'Sektörler yüklenemedi.');
      return [] as CatalogSector[];
    } finally {
      setSectorsLoading(false);
    }
  }, []);

  const loadCategories = useCallback(async (sectorId: number) => {
    setCategoriesLoading(true);
    try {
      setCategories(await CatalogService.getCategories(sectorId, true));
    } catch (err: any) {
      setError(err?.message || 'Kategoriler yüklenemedi.');
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setInfo(null);
    setDirty(false);
    setEditTarget(null);
    loadSectors();
  }, [open, loadSectors]);

  useEffect(() => {
    if (!open || selectedSectorId === null) {
      setCategories([]);
      return;
    }
    loadCategories(selectedSectorId);
  }, [open, selectedSectorId, loadCategories]);

  /** Üst kategoriler ve altları — 2 seviyeli ağacın render kaynağı. */
  const tree = useMemo(() => {
    const roots = categories.filter((c) => !c.parentId);
    const childrenOf = new Map<number, CatalogCategory[]>();
    categories.forEach((c) => {
      if (!c.parentId) return;
      if (!childrenOf.has(c.parentId)) childrenOf.set(c.parentId, []);
      childrenOf.get(c.parentId)!.push(c);
    });
    return roots.map((root) => ({ root, children: childrenOf.get(root.id) ?? [] }));
  }, [categories]);

  const markChanged = () => {
    setDirty(true);
    setError(null);
  };

  const handleClose = () => {
    if (dirty) onChanged();
    onClose();
  };

  // ─── Sektör işlemleri ─────────────────────────────
  const handleAddSector = async () => {
    const name = newSectorName.trim();
    if (!name) return;

    setSaving(true);
    setInfo(null);
    try {
      const created = await CatalogService.createSector(name);
      // Backend get-or-create çalışıyor; aynı adlı sektör varsa yenisi açılmaz.
      const alreadyExisted = sectors.some((s) => s.id === created.id);
      setInfo(
        alreadyExisted
          ? `"${created.name}" sektörü zaten vardı, seçildi.`
          : `"${created.name}" sektörü eklendi.`
      );
      setNewSectorName('');
      markChanged();
      await loadSectors();
      setSelectedSectorId(created.id);
    } catch (err: any) {
      setError(err?.message || 'Sektör eklenemedi.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddCategory = async () => {
    const name = newCategoryName.trim();
    if (!name || selectedSectorId === null) return;

    setSaving(true);
    setInfo(null);
    try {
      const created = await CatalogService.createCategory({
        sectorId: selectedSectorId,
        parentId: newCategoryParentId,
        name,
      });
      const alreadyExisted = categories.some((c) => c.id === created.id);
      setInfo(
        alreadyExisted
          ? `"${created.name}" kategorisi zaten vardı.`
          : `"${created.name}" kategorisi eklendi.`
      );
      setNewCategoryName('');
      setNewCategoryParentId(null);
      markChanged();
      await loadCategories(selectedSectorId);
    } catch (err: any) {
      setError(err?.message || 'Kategori eklenemedi.');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (kind: 'sector' | 'category', id: number, name: string) => {
    setEditTarget({ kind, id });
    setEditName(name);
    setError(null);
  };

  const handleSaveEdit = async () => {
    if (!editTarget) return;
    const name = editName.trim();
    if (!name) return;

    setSaving(true);
    setInfo(null);
    try {
      if (editTarget.kind === 'sector') {
        await CatalogService.updateSector({ id: editTarget.id, name });
        await loadSectors();
      } else {
        await CatalogService.updateCategory({ id: editTarget.id, name });
        if (selectedSectorId !== null) await loadCategories(selectedSectorId);
      }
      setInfo('Ad güncellendi. Bağlı ürünler etkilenmedi.');
      setEditTarget(null);
      markChanged();
    } catch (err: any) {
      setError(err?.message || 'Güncelleme başarısız.');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    setSaving(true);
    setInfo(null);
    try {
      if (deleteTarget.kind === 'sector') {
        await CatalogService.deleteSector(deleteTarget.id);
        await loadSectors();
        if (selectedSectorId === deleteTarget.id) setSelectedSectorId(null);
      } else {
        await CatalogService.deleteCategory(deleteTarget.id);
        if (selectedSectorId !== null) await loadCategories(selectedSectorId);
      }
      setInfo(`"${deleteTarget.name}" silindi.`);
      setDeleteTarget(null);
      markChanged();
    } catch (err: any) {
      // Dolu kayıtlarda backend kaç ürün/kategori bağlı olduğunu mesajda söylüyor.
      setError(err?.message || 'Silme başarısız.');
      setDeleteTarget(null);
    } finally {
      setSaving(false);
    }
  };

  /** Ad düzenleme satırı — sektör ve kategoride aynı görünüm. */
  const renderEditRow = (onCancel: () => void) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
      <TextField
        size="small"
        fullWidth
        autoFocus
        value={editName}
        onChange={(e) => setEditName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSaveEdit();
          if (e.key === 'Escape') onCancel();
        }}
      />
      <Tooltip title="Kaydet" arrow>
        <span>
          <IconButton size="small" color="primary" onClick={handleSaveEdit} disabled={saving}>
            <CheckIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <IconButton size="small" onClick={onCancel}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
          <AccountTreeIcon color="primary" />
          <Typography variant="h6" component="span" sx={{ flex: 1, fontWeight: 700 }}>
            Sektör ve Kategori Yönetimi
          </Typography>
          <IconButton size="small" onClick={handleClose} aria-label="Kapat">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {info && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>
              {info}
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* ─── Sektörler ─── */}
            <Grid item xs={12} md={5}>
              <Paper variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 1.5, pb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Sektörler
                  </Typography>
                </Box>
                <Divider />

                <Box sx={{ maxHeight: 340, overflowY: 'auto' }}>
                  {sectorsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <List dense disablePadding>
                      {sectors.map((sector) => {
                        const isEditing =
                          editTarget?.kind === 'sector' && editTarget.id === sector.id;
                        return (
                          <ListItemButton
                            key={sector.id}
                            selected={selectedSectorId === sector.id}
                            onClick={() => !isEditing && setSelectedSectorId(sector.id)}
                            sx={{ py: 0.5 }}
                          >
                            {isEditing ? (
                              renderEditRow(() => setEditTarget(null))
                            ) : (
                              <>
                                <ListItemText
                                  primary={sector.name}
                                  primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                                />
                                <Chip
                                  size="small"
                                  label={sector.productCount.toLocaleString('tr-TR')}
                                  sx={{ height: 18, fontSize: '0.65rem', mr: 0.5 }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    startEdit('sector', sector.id, sector.name);
                                  }}
                                >
                                  <EditIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteTarget({
                                      kind: 'sector',
                                      id: sector.id,
                                      name: sector.name,
                                    });
                                  }}
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </>
                            )}
                          </ListItemButton>
                        );
                      })}
                      {sectors.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                          Henüz sektör yok.
                        </Typography>
                      )}
                    </List>
                  )}
                </Box>

                <Divider />
                <Box sx={{ p: 1.5, display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Yeni sektör adı"
                    value={newSectorName}
                    onChange={(e) => setNewSectorName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddSector()}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddSector}
                    disabled={saving || !newSectorName.trim()}
                  >
                    Ekle
                  </Button>
                </Box>
              </Paper>
            </Grid>

            {/* ─── Kategoriler ─── */}
            <Grid item xs={12} md={7}>
              <Paper variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 1.5, pb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {selectedSector ? `${selectedSector.name} — Kategoriler` : 'Kategoriler'}
                  </Typography>
                  {!selectedSector && (
                    <Typography variant="caption" color="text.secondary">
                      Soldan bir sektör seçin.
                    </Typography>
                  )}
                </Box>
                <Divider />

                <Box sx={{ maxHeight: 340, overflowY: 'auto' }}>
                  {categoriesLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <List dense disablePadding>
                      {tree.map(({ root, children }) => (
                        <React.Fragment key={root.id}>
                          <ListItemButton sx={{ py: 0.5 }} disableRipple>
                            {editTarget?.kind === 'category' && editTarget.id === root.id ? (
                              renderEditRow(() => setEditTarget(null))
                            ) : (
                              <>
                                <ListItemText
                                  primary={root.name}
                                  primaryTypographyProps={{
                                    variant: 'body2',
                                    fontWeight: 600,
                                    noWrap: true,
                                  }}
                                />
                                <Chip
                                  size="small"
                                  label={root.productCount.toLocaleString('tr-TR')}
                                  sx={{ height: 18, fontSize: '0.65rem', mr: 0.5 }}
                                />
                                <Tooltip title="Alt kategori ekle" arrow>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => setNewCategoryParentId(root.id)}
                                  >
                                    <SubdirectoryArrowRightIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                                <IconButton
                                  size="small"
                                  onClick={() => startEdit('category', root.id, root.name)}
                                >
                                  <EditIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() =>
                                    setDeleteTarget({
                                      kind: 'category',
                                      id: root.id,
                                      name: root.name,
                                    })
                                  }
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </>
                            )}
                          </ListItemButton>

                          {children.map((child) => (
                            <ListItemButton key={child.id} sx={{ py: 0.25, pl: 4 }} disableRipple>
                              {editTarget?.kind === 'category' && editTarget.id === child.id ? (
                                renderEditRow(() => setEditTarget(null))
                              ) : (
                                <>
                                  <SubdirectoryArrowRightIcon
                                    sx={{ fontSize: 14, mr: 0.5, color: 'text.disabled' }}
                                  />
                                  <ListItemText
                                    primary={child.name}
                                    primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                                  />
                                  <Chip
                                    size="small"
                                    label={child.productCount.toLocaleString('tr-TR')}
                                    sx={{ height: 18, fontSize: '0.65rem', mr: 0.5 }}
                                  />
                                  <IconButton
                                    size="small"
                                    onClick={() => startEdit('category', child.id, child.name)}
                                  >
                                    <EditIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() =>
                                      setDeleteTarget({
                                        kind: 'category',
                                        id: child.id,
                                        name: child.name,
                                      })
                                    }
                                  >
                                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </>
                              )}
                            </ListItemButton>
                          ))}
                        </React.Fragment>
                      ))}
                      {selectedSector && tree.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                          Bu sektörde kategori yok.
                        </Typography>
                      )}
                    </List>
                  )}
                </Box>

                <Divider />
                <Box sx={{ p: 1.5 }}>
                  {newCategoryParentId !== null && (
                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mb: 1 }}
                      label={`Alt kategori: ${
                        categories.find((c) => c.id === newCategoryParentId)?.name ?? ''
                      }`}
                      onDelete={() => setNewCategoryParentId(null)}
                    />
                  )}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder={
                        newCategoryParentId !== null ? 'Yeni alt kategori adı' : 'Yeni kategori adı'
                      }
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                      disabled={!selectedSector}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleAddCategory}
                      disabled={saving || !selectedSector || !newCategoryName.trim()}
                    >
                      Ekle
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button variant="contained" onClick={handleClose}>
            Kapat
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Silme onayı ─── */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Silmeyi onayla</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{deleteTarget?.name}</strong> silinecek. Bağlı ürün veya alt kategori varsa
            işlem reddedilir; önce onları başka bir yere taşımanız gerekir.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Vazgeç</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete} disabled={saving}>
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CatalogTaxonomyModal;
